import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import type { ImpactBundleTrail, SecondOrderSnapshot } from '@akem/shared';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import {
  formatConfidence,
  formatMagnitude,
  getSecondOrderSnapshot,
  resolveInstrumentId,
  splitTrails,
} from '../../../services/secondOrder.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import type { WidgetProps } from '../types/index.js';
import { ExposureSnapshot } from '../components/ExposureSnapshot.js';

interface ExplorerData {
  instrumentId: string | null;
  snapshot: SecondOrderSnapshot | null;
  trails: ImpactBundleTrail[];
}

const normalizeExplanationStep = (step: any): string | null => {
  if (!step) return null;
  if (typeof step === 'string') return step;
  if (typeof step.note === 'string' && step.note.trim()) return step.note.trim();
  if (typeof step.event === 'string' && step.event.trim()) return step.event.trim();
  if (typeof step.edge === 'string' && step.edge.trim()) return step.edge.trim();
  return null;
};

const renderExplanation = (trail: ImpactBundleTrail) => {
  const steps = Array.isArray(trail.bundle.explanation_path)
    ? trail.bundle.explanation_path
        .map((step: any) => normalizeExplanationStep(step))
        .filter((value): value is string => Boolean(value))
    : [];
  if (steps.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Explanation pending.
      </Typography>
    );
  }
  return (
    <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
      {steps.map((step, index) => (
        <Typography key={`${trail.eventId}-step-${index}`} component="li" variant="body2" color="text.secondary">
          {step}
        </Typography>
      ))}
    </Stack>
  );
};

const tierLabel = (tier: ImpactBundleTrail['tier']) => (tier === 'fast' ? 'Fast stream' : 'Slow stream');

const TrailAccordion: React.FC<{ trail: ImpactBundleTrail; defaultExpanded?: boolean }> = ({ trail, defaultExpanded }) => {
  const magnitude = formatMagnitude(trail.bundle.magnitude, trail.bundle.units);
  const confidence = formatConfidence(trail.bundle.confidence);
  const anchored = Boolean(trail.bundle.anchored_by_bedrock);
  const tier = tierLabel(trail.tier);
  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded} elevation={0} sx={{ bgcolor: 'transparent' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1 }}>
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {trail.bundle.affected_metric}
            </Typography>
            <Chip size="small" color={anchored ? 'success' : 'warning'} label={anchored ? 'Anchored' : 'Signal'} />
            <Chip
              size="small"
              variant="outlined"
              icon={<ThunderstormIcon fontSize="small" color={trail.tier === 'fast' ? 'primary' : 'action'} />}
              label={tier}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" color={trail.bundle.magnitude >= 0 ? 'success.main' : 'error.main'}>
              {magnitude}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lag {trail.bundle.lag}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confidence {confidence}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {new Date(trail.asOf.split('|')[0]).toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Source {trail.source} | Event {trail.eventId}
        </Typography>
        {renderExplanation(trail)}
        {trail.bundle.evidence && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Evidence: {trail.bundle.evidence.events ?? 0} events, {trail.bundle.evidence.unique_domains ?? 0} unique domains.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export const ImpactExplorerWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateState,
  onUpdateOutputs,
}) => {
  const config = (widget.config || {}) as Record<string, unknown>;
  const profileId = typeof config.profileId === 'string' ? config.profileId : null;
  const ticker = typeof config.ticker === 'string' ? config.ticker : null;
  const instrumentId = React.useMemo(
    () =>
      resolveInstrumentId({
        instrumentId: typeof config.instrumentId === 'string' ? (config.instrumentId as string) : null,
        profileId,
        ticker,
      }),
    [config.instrumentId, profileId, ticker]
  );

  const fetchData = React.useCallback(async (): Promise<ExplorerData> => {
    if (!instrumentId) {
      return { instrumentId: null, snapshot: null, trails: [] };
    }
    const res = await getSecondOrderSnapshot(instrumentId, { limit: 60 });
    if (!res) {
      return { instrumentId, snapshot: null, trails: [] };
    }
    return { instrumentId, snapshot: res.snapshot, trails: res.trails };
  }, [instrumentId]);

  const { data, loading, error, refresh } = useWidgetData<ExplorerData>({
    widget,
    onUpdateState,
    fetchData,
    refreshInterval: 180000,
    dependencies: [instrumentId],
  });

  const [view, setView] = React.useState<'anchored' | 'signal'>('anchored');

  React.useEffect(() => {
    if (view === 'signal' && (!data?.trails || splitTrails(data.trails).signalOnly.length === 0)) {
      setView('anchored');
    }
  }, [data?.trails, view]);

  const grouped = React.useMemo(() => {
    const trails = data?.trails ?? [];
    const { anchored, signalOnly } = splitTrails(trails);
    const active = view === 'anchored' ? anchored : signalOnly;
    const fast = active.filter((trail) => trail.tier === 'fast');
    const slow = active.filter((trail) => trail.tier === 'slow');
    return { anchored, signalOnly, active, fast, slow };
  }, [data?.trails, view]);

  React.useEffect(() => {
    if (!onUpdateOutputs) return;
    const trails = data?.trails ?? [];
    const { anchored, signalOnly } = splitTrails(trails);
    const all = [...anchored, ...signalOnly];
    const preferred = all[0] ?? null;

    const summary = (trail: ImpactBundleTrail | null): string | null => {
      if (!trail) return null;
      const steps = Array.isArray(trail.bundle.explanation_path)
        ? trail.bundle.explanation_path
            .map((step: any) => normalizeExplanationStep(step))
            .filter((value): value is string => Boolean(value))
        : [];
      if (steps.length === 0) return `${trail.bundle.affected_metric} impact`;
      return steps.slice(0, 2).join(' → ');
    };

    const sentiment = anchored.length > signalOnly.length
      ? 'bullish'
      : signalOnly.length > anchored.length
      ? 'bearish'
      : 'neutral';

    onUpdateOutputs({
      selectedImpact: preferred
        ? {
            type: preferred.tier === 'fast' ? 'market' : 'sector',
            title: preferred.bundle.affected_metric,
            description: summary(preferred),
            confidence: preferred.bundle.confidence ?? 0,
            impact:
              Math.abs(preferred.bundle.magnitude) >= 0.5
                ? 'high'
                : Math.abs(preferred.bundle.magnitude) >= 0.2
                ? 'medium'
                : 'low',
            affectedStocks: [],
          }
        : null,
      filteredImpacts: all.map((trail) => ({
        type: trail.tier === 'fast' ? 'market' : 'sector',
        title: trail.bundle.affected_metric,
        impact: trail.bundle.anchored_by_bedrock ? 'high' : 'medium',
      })),
      marketSentiment: sentiment,
      analyzedStock: null,
      summary: summary(preferred),
    });
  }, [data?.trails, onUpdateOutputs]);

  const noInstrument = !instrumentId;
  const noData = (grouped.active.length === 0 && !loading && !error) as boolean;

  return (
    <Stack spacing={1.5} sx={{ height: '100%', px: 1, py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Impact Explorer
        </Typography>
        <Tabs
          value={view}
          onChange={(_, next) => setView(next)}
          variant="standard"
          sx={{ minHeight: 36 }}
        >
          <Tab value="anchored" label={`Anchored (${grouped.anchored.length})`} sx={{ minHeight: 36 }} />
          <Tab
            value="signal"
            label={`Signal-only (${grouped.signalOnly.length})`}
            sx={{ minHeight: 36 }}
            disabled={grouped.signalOnly.length === 0}
          />
        </Tabs>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refresh()} disabled={loading || noInstrument}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {noInstrument ? (
        <Alert severity="info">Provide a profile or ticker to explore second-order impacts.</Alert>
      ) : loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : noData ? (
        <Alert severity="info">No {view === 'anchored' ? 'anchored' : 'signal-only'} trails yet.</Alert>
      ) : (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
          {grouped.fast.length > 0 && (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2">Fast streams</Typography>
              <Divider flexItem />
              {grouped.fast.map((trail, idx) => (
                <TrailAccordion key={`fast-${trail.eventId}-${idx}`} trail={trail} defaultExpanded={idx === 0} />
              ))}
            </Stack>
          )}
          {grouped.slow.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Slow streams</Typography>
              <Divider flexItem />
              {grouped.slow.map((trail, idx) => (
                <TrailAccordion key={`slow-${trail.eventId}-${idx}`} trail={trail} defaultExpanded={grouped.fast.length === 0 && idx === 0} />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {view === 'signal' && grouped.signalOnly.length > 0 && (
        <Alert severity="warning" variant="outlined">
          Signal-only impacts need stronger exposure coverage before they can be steward-approved.
        </Alert>
      )}

      {instrumentId ? (
        <ExposureSnapshot instrumentId={instrumentId} />
      ) : null}
    </Stack>
  );
};
